import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const SWIPE_PX = 48;
/** Past this a phone photo is grain, and the pan bounds get hard to reason about. */
const MAX_SCALE = 4;
/** What a double tap jumps to — enough to read a serial number off a boiler. */
const TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 320;
/** A tap that moves less than this is a tap, not a drag. */
const TAP_SLOP_PX = 10;
const FIT = { scale: 1, x: 0, y: 0 };

const distance = (touches) => Math.hypot(
  touches[0].clientX - touches[1].clientX,
  touches[0].clientY - touches[1].clientY,
);

/**
 * Full-screen viewer for a set of photos, with pinch-zoom and pan.
 *
 * Built from the app's own overlay idiom (`fixed inset-0 z-50 bg-worker/…`),
 * not from a gallery library: a library would own the history and focus
 * behaviour this file has to control anyway, and the gesture half is the sixty
 * lines below.
 *
 * **Back closes the viewer, it does not leave the page.** Opening pushes one
 * history entry; `popstate` closes; and closing by any other route pops that
 * entry back off on unmount, so the stack is exactly as deep as it was. Same
 * care as Chat's mobile list→thread push — a viewer that walked the reader out
 * of the job is worse than no viewer.
 *
 * **T18-M1: zoom and pan, and why the viewer existing was not enough.** Daniel,
 * quoting a job on a real phone, could not see the photo whole. The viewer was
 * already here and already reached from the job's gallery, and it already drew
 * the picture `object-contain` — but fitted to the screen is not the same as
 * legible. A photo of a leak under a sink, shrunk to a phone, is a grey
 * rectangle; what the FixyBee needs is to get close enough to price the job,
 * and there was no way to get closer. So the gap was never the lightbox, it
 * was that the lightbox had exactly one zoom level.
 *
 * The gestures are the platform's, driven by three numbers (`scale`, `x`, `y`)
 * on one `transform`. No library, and **no `preventDefault`**: React attaches
 * `touchmove` passively at the root, so a handler calling it is a handler that
 * silently does nothing. `touch-action: none` on the overlay is what actually
 * stops the browser claiming the gesture, and it is declared where the gesture
 * is read.
 *
 * One finger means two different things and the scale decides which: at fit it
 * swipes between photos, zoomed in it pans. That is the messaging-app
 * convention and it is also the only one that leaves both gestures reachable.
 *
 * Props:
 *   urls: string[]        every photo, in display order
 *   index: number | null  which one is open; null renders nothing
 *   onIndex: (i) => void  move within the set
 *   onClose: () => void
 */
export default function PhotoLightbox({ urls = [], index, onIndex, onClose }) {
  const { t } = useTranslation();
  const open = index !== null && index !== undefined && urls.length > 0;
  const count = urls.length;

  const [view, setView] = useState(FIT);
  const viewRef = useRef(view);
  viewRef.current = view;

  // The listeners are registered once per open; reading the live callbacks
  // through refs keeps them from being torn down on every index change, which
  // is what would push a second history entry per swipe.
  const onCloseRef = useRef(onClose);
  const onIndexRef = useRef(onIndex);
  const indexRef = useRef(index);
  onCloseRef.current = onClose;
  onIndexRef.current = onIndex;
  indexRef.current = index;

  const imgRef = useRef(null);
  // The picture's size at scale 1. Every pan bound is computed from it, and it
  // is remeasured rather than assumed: `object-contain` decides it from the
  // photo's own aspect ratio and the screen, so it is different for every
  // photo and changes when the phone is turned.
  const baseRef = useRef({ w: 0, h: 0 });
  const measureBase = useCallback(() => {
    const el = imgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const scale = viewRef.current.scale || 1;
    baseRef.current = { w: rect.width / scale, h: rect.height / scale };
  }, []);

  /** Keep the picture covering the screen: never drag empty space into view. */
  const settle = useCallback((scale, x, y) => {
    const next = Math.min(MAX_SCALE, Math.max(1, scale));
    if (next <= 1) return FIT;
    const { w, h } = baseRef.current;
    const maxX = Math.max(0, (w * next - window.innerWidth) / 2);
    const maxY = Math.max(0, (h * next - window.innerHeight) / 2);
    return {
      scale: next,
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  const zoomTo = useCallback((scale) => {
    setView((v) => settle(scale, v.x, v.y));
  }, [settle]);

  const step = useCallback((delta) => {
    if (count < 2) return;
    const next = (indexRef.current + delta + count) % count;
    onIndexRef.current?.(next);
  }, [count]);

  // A new photo is a new fit. Without this, opening a second picture inherits
  // the first one's zoom and lands somewhere in its middle.
  useEffect(() => { setView(FIT); }, [index]);

  // Escape closes, arrows move, and the keyboard reaches the zoom too — it is
  // the only way to it without a touchscreen or a mouse.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCloseRef.current?.(); }
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "+" || e.key === "=") zoomTo(viewRef.current.scale + 0.5);
      else if (e.key === "-") zoomTo(viewRef.current.scale - 0.5);
      else if (e.key === "0") setView(FIT);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step, zoomTo]);

  // The page behind must not scroll under the overlay.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  // Turning the phone changes what "fit" means, so the bounds computed from
  // the old one would let the picture be dragged off the screen.
  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => { setView(FIT); measureBase(); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, measureBase]);

  // The android back gesture. `pushed` guards the pop: when the close came
  // from popstate the entry is already gone and calling back() again would
  // take the reader off the job page — the exact bug this is here to avoid.
  const pushed = useRef(false);
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ fixyLightbox: true }, "");
    pushed.current = true;
    const onPop = () => { pushed.current = false; onCloseRef.current?.(); };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (pushed.current) { pushed.current = false; window.history.back(); }
    };
  }, [open]);

  // ---- gestures -----------------------------------------------------------
  const gesture = useRef({});
  const lastTap = useRef(0);

  const onTouchStart = (e) => {
    const ts = e.touches;
    const g = gesture.current;
    g.moved = false;
    g.startX = viewRef.current.x;
    g.startY = viewRef.current.y;
    if (ts.length === 2) {
      g.mode = "pinch";
      g.startDist = distance(ts) || 1;
      g.startScale = viewRef.current.scale;
      measureBase();
    } else if (ts.length === 1) {
      g.mode = viewRef.current.scale > 1 ? "pan" : "swipe";
      g.pointX = ts[0].clientX;
      g.pointY = ts[0].clientY;
    }
  };

  const onTouchMove = (e) => {
    const ts = e.touches;
    const g = gesture.current;
    if (g.mode === "pinch" && ts.length === 2) {
      g.moved = true;
      setView(settle(g.startScale * (distance(ts) / g.startDist), g.startX, g.startY));
    } else if (g.mode === "pan" && ts.length === 1) {
      g.moved = true;
      setView(settle(
        viewRef.current.scale,
        g.startX + (ts[0].clientX - g.pointX),
        g.startY + (ts[0].clientY - g.pointY),
      ));
    }
  };

  const onTouchEnd = (e) => {
    const g = gesture.current;
    const touch = e.changedTouches[0];
    const dx = (touch?.clientX ?? 0) - (g.pointX ?? 0);
    const dy = (touch?.clientY ?? 0) - (g.pointY ?? 0);

    if (g.mode === "swipe" && Math.abs(dx) >= SWIPE_PX) {
      g.moved = true;
      step(dx < 0 ? 1 : -1);
    } else if (g.mode !== "pinch"
               && Math.abs(dx) < TAP_SLOP_PX && Math.abs(dy) < TAP_SLOP_PX
               // Only on the picture. A double tap on the backdrop is two
               // requests to close, and the first one already closed it.
               && imgRef.current?.contains(e.target)) {
      const now = Date.now();
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0;
        g.moved = true;            // so the click that follows does not close
        measureBase();
        setView((v) => (v.scale > 1 ? FIT : settle(TAP_SCALE, 0, 0)));
      } else {
        lastTap.current = now;
      }
    }
    g.mode = null;
  };

  // A pan or a pinch ends with a click the browser sends anyway. Without this
  // guard, letting go of a drag over the backdrop closes the viewer.
  const onBackdropClick = () => {
    if (gesture.current.moved) { gesture.current.moved = false; return; }
    onClose?.();
  };

  if (!open) return null;
  const url = urls[index];
  if (!url) return null;

  const zoomed = view.scale > 1;
  const arrow =
    "absolute top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] flex items-center justify-center " +
    "rounded-full bg-cream/15 text-cream hover:bg-cream/30 transition-colors";

  return (
    <div
      className="fixed inset-0 z-50 bg-worker/95 flex items-center justify-center p-4 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label={t("lightbox.label")}
      data-testid="photo-lightbox"
      // The gesture is ours, all of it. React registers `touchmove` passively
      // at the root, so `preventDefault` is not available to take it back —
      // this declaration is what stops the browser scrolling and page-zooming
      // under the handlers above.
      style={{ touchAction: "none" }}
      onClick={onBackdropClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t("lightbox.close")}
        className="absolute top-4 right-4 z-10 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full bg-cream/15 text-cream hover:bg-cream/30 transition-colors"
        data-testid="photo-lightbox-close"
      >
        <X size={22} />
      </button>

      {/* Hidden while zoomed in: at that point a horizontal drag is a pan, so
          an arrow beside the picture invites a gesture that no longer does
          what it looks like. They come back at fit. */}
      {count > 1 && !zoomed && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            aria-label={t("lightbox.prev")}
            className={`${arrow} left-3 z-10`}
            data-testid="photo-lightbox-prev"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(1); }}
            aria-label={t("lightbox.next")}
            className={`${arrow} right-3 z-10`}
            data-testid="photo-lightbox-next"
          >
            <ChevronRight size={26} />
          </button>
          <span
            className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 rounded-full bg-cream/15 px-3 py-1 font-mono text-xs text-cream"
            data-testid="photo-lightbox-counter"
          >
            {t("lightbox.counter", { current: index + 1, total: count })}
          </span>
        </>
      )}

      {/* `max-h-full` and not `85vh`: the cap is the box this picture is
          actually in, which is the padded overlay. A `vh` ceiling is the LARGE
          viewport on a phone — taller than the screen while the browser's
          toolbar is up — and AUDIT §61 is what that costs. */}
      <img
        ref={imgRef}
        src={mediaUrl(url)}
        alt={t("lightbox.photoAlt", { current: index + 1, total: count })}
        className="max-h-full max-w-full object-contain rounded-sm select-none"
        style={{
          transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
          // Follows the finger while a gesture is live, eases only when the
          // view is set in one go (a double tap, a key, letting go past the
          // ceiling). A transition during a pinch lags a frame behind the
          // fingers and reads as the picture sticking.
          transition: gesture.current.mode ? "none" : "transform 180ms var(--ease-out)",
          cursor: zoomed ? "grab" : "zoom-in",
          willChange: "transform",
          // Dragging the file out of the page instead of panning it.
          WebkitUserDrag: "none",
        }}
        draggable={false}
        onLoad={measureBase}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.stopPropagation();
          measureBase();
          setView((v) => (v.scale > 1 ? FIT : settle(TAP_SCALE, 0, 0)));
        }}
        data-testid="photo-lightbox-image"
      />
    </div>
  );
}
